import React from 'react';
import { IonItem, IonLabel } from '@ionic/react';
import SectionCard from '@/components/SectionCard';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import type { Registration } from '../types';

interface RegistrationBlockProps {
  topicId: string;
  registration: Registration | null;
  /** = features.topicRegistrationEnabled */
  registrationEnabled: boolean;
  /** Re-fetch topic info after a guard unlock (= fetchTopicInfo). */
  onGuardSuccess: () => void;
}

const formatDate = (dateString: string | undefined): string => {
  try {
    if (!dateString) return 'recently';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'recently';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'recently';
  }
};

/**
 * The "Register for Topic" / "Edit Registration" entry, gated on the
 * `topicRegistration` feature flag. Wrapped in an `ActionGuard` so it locks
 * until the viewer has verified enough to register.
 */
const RegistrationBlock: React.FC<RegistrationBlockProps> = ({
  topicId,
  registration,
  registrationEnabled,
  onGuardSuccess,
}) => {
  if (!registrationEnabled) {
    return null;
  }

  return (
    <ActionGuardContainer
      toastConfig={{
        helpText: {
          singleVerification:
            'Verify yourself to interact with this topic. [Verify]',
          updateProfile:
            'Update your profile to interact with this topic. [Update]',
          setupProfile:
            'Set up your profile to interact with this topic. [Set up]',
        },
        frequency: 'always',
      }}
      onSuccess={onGuardSuccess}
    >
      <SectionCard>
        <ActionGuard
          disableMode="locked"
          action={ACTIONS.CREATE}
          resource={RESOURCES.TOPIC_REGISTRATIONS}
          helpText={{
            singleVerification: 'Verify your email to register for this topic',
            updateProfile: 'Update your profile to register for this topic',
            setupProfile: 'Set up your profile to register for this topic',
          }}
          guardedElem={
            <IonItem
              routerLink={`/topics/${encodeURIComponent(topicId)}/register`}
              button
              detail
              lines="none"
              data-testid="register-list-item"
            >
              <IonLabel>
                <h2 style={{ fontWeight: '600' }}>
                  {registration ? 'Edit Registration' : 'Register for Topic...'}
                </h2>
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--ion-color-medium)',
                    whiteSpace: 'normal',
                  }}
                >
                  {registration
                    ? `Registered on ${formatDate(registration.registeredAt)}`
                    : 'Registering will help us track our exponential growth goals and allow you to start receiving updates.'}
                </p>
              </IonLabel>
            </IonItem>
          }
        />
      </SectionCard>
    </ActionGuardContainer>
  );
};

export default RegistrationBlock;
